import { isResourceDefinitionOf } from "@@/shared/acquire/resources/resource-definition-helpers";
import { isResourceFoundationOf } from "@@/shared/acquire/resources/resource-foundation-helpers";
import { resourceFoundationIdZodSchema } from "@@/shared/acquire/resources/resource-foundation-id";
import type {
    MatchingResourceParts} from "@@/shared/acquire/resources/resource-parts";
import {
    isMatchingResourceParts
} from "@@/shared/acquire/resources/resource-parts";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { metaContentZodSchema } from "@@/shared/meta-content/meta-content";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { availableQuantityZodSchema } from "@@/shared/units-and-quantities/quantity";
import { unitZodSchema } from "@@/shared/units-and-quantities/unit";
import { z } from "zod";
import { resourceIdFactory, resourceIdZodSchema } from "./resource-id";
import type {
    ResourceTableGroup} from "./resource-table-group";
import {
    isResourceTableGroup
} from "./resource-table-group";
import { resourceShortNameZodString } from "./resource.short-name";

const resourceTableZodObject = z.object({
    _id: resourceIdZodSchema,
    _createdAt: isoAndUnixZodSchema,
    _type: z.literal("RESOURCE_TABLE"),
    ownerId: providerIdZodSchema,
    meta: metaContentZodSchema,
    quantity: availableQuantityZodSchema,
    unit: unitZodSchema,
    /** If `foundationId is set, the resource was generated
     * using a foundation and a definition. Foundation could be edited and
     * all resources with that foundation would then be changed, if value
     * edited had not already been overiden in their definitions that is */
    foundationId: resourceFoundationIdZodSchema.optional(),
    /** If `true` `foundationId` has to be available and the definition is
     * departing from the foundation in some ways */
    hasDefinitionOverrides: z.coerce.boolean().optional(),
    /** If `true` the resource can be checked in.
     * If `false` the resource can not be checked in.
     * If not set, it defaults to `true`. */
    checkInEnabled: z.coerce.boolean().optional(),
    /** If `true` the resource allows unlimited number
     * of check ins to be true at once.
     * If `false` the resource can only be checked in
     * by one reservation at a time.
     * If not set, it defaults to `false`. */
    allowMultipleCheckIns: z.coerce.boolean().optional(),
    /**
     * Optional short name, e.g. B12
     * if not set a short name will be generated based on
     * the title when needed. Max 5 chars.
     */
    shortName: resourceShortNameZodString.optional(),
});

type ResourceTable = z.infer<typeof resourceTableZodObject>;

const isResourceTable = (item: unknown): item is ResourceTable => {
    return (item as ResourceTable)?._type === "RESOURCE_TABLE";
};
const isResourceTableOrTableGroup = (
    item: unknown,
): item is ResourceTable | ResourceTableGroup => {
    return isResourceTable(item) || isResourceTableGroup(item);
};

const _resourceTableFactoryParamsZodObject = resourceTableZodObject
    .omit({
        _id: true,
        _type: true,
        _createdAt: true,
    })
    .and(z.object({ _id: resourceIdZodSchema.optional() }));
type ResourceTableFactoryParams = z.infer<
    typeof _resourceTableFactoryParamsZodObject
>;

const isResourceTableParts =
    isMatchingResourceParts<ResourceTable>("RESOURCE_TABLE");
const isResourceTableFoundation =
    isResourceFoundationOf<ResourceTable>("RESOURCE_TABLE");
const isResourceTableDefinition =
    isResourceDefinitionOf<ResourceTable>("RESOURCE_TABLE");

function resourceTableFactory(
    params: ResourceTableFactoryParams,
): ResourceTable;
function resourceTableFactory(
    params: MatchingResourceParts<ResourceTable>,
): ResourceTable;
function resourceTableFactory(
    params: ResourceTableFactoryParams | MatchingResourceParts<ResourceTable>,
): ResourceTable;
function resourceTableFactory(
    params: ResourceTableFactoryParams | MatchingResourceParts<ResourceTable>,
): ResourceTable {
    if (isResourceTableParts(params)) {
        // MatchingResourceParts<ResourceTable>
        const foundation = params.foundation;
        const definition = params.definition;
        if (foundation._id !== definition.foundationId) {
            throw new Error("Definition must be based on the given foundation");
        }
        const resource: ResourceTable = {
            ...foundation.data,
            meta: {
                ...foundation.data.meta,
                ...(definition.data?.title
                    ? {
                          title: definition.data.title,
                      }
                    : {}),
                ...(definition.data?.subtitle
                    ? {
                          subtitle: definition.data.subtitle,
                      }
                    : {}),
                ...(definition.data?.description
                    ? {
                          description: definition.data.description,
                      }
                    : {}),
            },
            ...(definition.data?.quantity
                ? {
                      quantity: definition.data.quantity,
                  }
                : {}),
            hasDefinitionOverrides: definition.hasOverrides,
            foundationId: definition.foundationId,
            _id: definition._id,
        };
        return resource;
    }

    // ResourceTableFactoryParams
    const resource: ResourceTable = {
        ...params,
        _id: params._id ?? resourceIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "RESOURCE_TABLE",
    };
    return resource;
}

export {
    isResourceTable,
    isResourceTableDefinition,
    isResourceTableFoundation,
    isResourceTableOrTableGroup,
    isResourceTableParts,
    resourceTableFactory,
    resourceTableZodObject,
};
export type { ResourceTable, ResourceTableFactoryParams };
